import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CreditCard, FileText, Wallet } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { employerCockpitApi, ARBO_POT_KEY, ACTIEPUNTEN_KEY } from '../services/employerCockpitApi';

const PRESETS = [500, 1000, 2500, 5000];
const euro = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);

/**
 * Arbo-pot (migratie 187): prepaid tegoed van de opdrachtgever. Opwaarderen via
 * Stripe Checkout; reguliere facturen in één klik uit de pot betalen.
 * Bereikbaar via het actiepunt 'Wallet restock' en de link op Actiepunten.
 */
export const EmployerArboPotPage: React.FC = () => {
  const { t, i18n } = useTranslation('employerDashboard');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  useCockpitTitle(t('arboPot.titel'), t('arboPot.ondertitel'));

  const terugVanStripe = searchParams.get('opwaardering');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ARBO_POT_KEY,
    queryFn: employerCockpitApi.arboPot,
    // Na terugkeer van Stripe even pollen: de webhook schrijft het saldo bij.
    refetchInterval: terugVanStripe === 'gelukt' ? 3_000 : false,
  });

  useEffect(() => {
    if (!terugVanStripe) return;
    if (terugVanStripe === 'gelukt') toast.success(t('arboPot.gelukt'), { duration: 6000 });
    else toast(t('arboPot.afgebroken'));
    // Na 30 s stoppen met pollen door de parameter weg te halen.
    const timer = window.setTimeout(() => setSearchParams({}, { replace: true }), 30_000);
    return () => window.clearTimeout(timer);
  }, [terugVanStripe, setSearchParams, t]);

  const [bedrag, setBedrag] = useState<number | ''>(1000);
  const [richtprijs, setRichtprijs] = useState<string>('');
  useEffect(() => {
    if (data && !data.richtprijs_is_standaard) setRichtprijs(String(data.richtprijs));
  }, [data?.richtprijs, data?.richtprijs_is_standaard]); // eslint-disable-line react-hooks/exhaustive-deps

  const opwaarderen = useMutation({
    mutationFn: (b: number) => employerCockpitApi.opwaarderen(b),
    onSuccess: ({ checkout_url }) => {
      window.location.href = checkout_url;
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('actiepunten.fout')),
  });

  const ververs = () => {
    queryClient.invalidateQueries({ queryKey: ARBO_POT_KEY });
    queryClient.invalidateQueries({ queryKey: ACTIEPUNTEN_KEY });
  };

  const zetRichtprijs = useMutation({
    mutationFn: (r: number | null) => employerCockpitApi.zetRichtprijs(r),
    onSuccess: () => {
      toast.success(t('arboPot.opgeslagen'));
      ververs();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('actiepunten.fout')),
  });

  const betaal = useMutation({
    mutationFn: (id: string) => employerCockpitApi.betaalFactuur(id),
    onSuccess: (r) => {
      toast.success(t('arboPot.betaald', { nummer: r.invoice_number }));
      ververs();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('actiepunten.fout')),
  });

  const datumF = useMemo(
    () => new Intl.DateTimeFormat(i18n.language?.startsWith('en') ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }),
    [i18n.language]
  );

  if ((error as any)?.response?.status === 403) {
    return <p className="p-6 text-sm text-navy-600">{t('arboPot.geenToegang')}</p>;
  }
  if (isError) {
    return (
      <div className="m-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        {t('actiepunten.fout')}
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="p-6 space-y-3">
        <div className="h-32 rounded-xl bg-gray-200 animate-pulse" />
        <div className="h-48 rounded-xl bg-gray-200 animate-pulse" />
      </div>
    );
  }

  const geldigBedrag = typeof bedrag === 'number' && bedrag >= data.opwaarderen_min && bedrag <= data.opwaarderen_max;
  const vulling = Math.min(100, data.drempel > 0 ? (data.saldo / data.drempel) * 100 : 100);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Saldo */}
        <section className={`rounded-xl border bg-white shadow-sm p-5 ${data.onder_drempel ? 'border-red-200' : 'border-gray-200'}`}>
          <p className="text-xs font-semibold uppercase tracking-widest text-navy-400 flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            {t('arboPot.saldo')}
          </p>
          <p className="mt-2 text-3xl font-bold text-navy-900">{euro(data.saldo)}</p>
          <p className="text-sm text-navy-600">{t('arboPot.dekt', { count: data.trajecten_gedekt })}</p>
          <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden" aria-hidden>
            <div className={`h-full ${data.onder_drempel ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${vulling}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-navy-500">{t('arboPot.drempel', { trajecten: data.drempel_trajecten, bedrag: euro(data.drempel) })}</p>
          {data.onder_drempel && (
            <p className="mt-2 text-sm font-medium text-red-700 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" />
              {t('arboPot.onderDrempel')}
            </p>
          )}
        </section>

        {/* Opwaarderen */}
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-semibold text-navy-900 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            {t('arboPot.opwaarderen')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setBedrag(p)}
                className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${
                  bedrag === p ? 'border-navy-900 bg-navy-900 text-white' : 'border-gray-300 text-navy-800 hover:border-gray-400'
                }`}
              >
                {euro(p)}
              </button>
            ))}
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-navy-600">{t('arboPot.anderBedrag')}</span>
            <input
              type="number"
              min={data.opwaarderen_min}
              max={data.opwaarderen_max}
              step={50}
              value={bedrag}
              onChange={(e) => setBedrag(e.target.value === '' ? '' : Number(e.target.value))}
              className="mt-1 w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          {!geldigBedrag && (
            <p className="text-xs text-red-700">{t('arboPot.bedragFout', { min: euro(data.opwaarderen_min), max: euro(data.opwaarderen_max) })}</p>
          )}
          <button
            type="button"
            disabled={!geldigBedrag || opwaarderen.isPending}
            onClick={() => geldigBedrag && opwaarderen.mutate(bedrag as number)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
          >
            {t('arboPot.naarStripe')}
          </button>

          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs font-medium text-navy-600">
              {t('arboPot.richtprijs')} {data.richtprijs_is_standaard && <span className="text-navy-400">({t('arboPot.standaard')}: {euro(data.richtprijs)})</span>}
            </p>
            <p className="text-xs text-navy-400">{t('arboPot.richtprijsUitleg')}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <input
                type="number"
                min={1}
                step={25}
                placeholder={String(data.richtprijs)}
                value={richtprijs}
                onChange={(e) => setRichtprijs(e.target.value)}
                className="w-32 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
              />
              <button
                type="button"
                disabled={!richtprijs || Number(richtprijs) <= 0 || zetRichtprijs.isPending}
                onClick={() => zetRichtprijs.mutate(Number(richtprijs))}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-navy-800 hover:border-gray-400 disabled:opacity-50"
              >
                {t('arboPot.opslaan')}
              </button>
              {!data.richtprijs_is_standaard && (
                <button
                  type="button"
                  disabled={zetRichtprijs.isPending}
                  onClick={() => {
                    setRichtprijs('');
                    zetRichtprijs.mutate(null);
                  }}
                  className="text-xs font-medium text-navy-600 hover:text-navy-900"
                >
                  {t('arboPot.herstel')}
                </button>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Openstaande facturen */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <h2 className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-navy-900 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          {t('arboPot.openFacturen')}
        </h2>
        {data.open_facturen.length === 0 ? (
          <p className="p-4 text-sm text-navy-500">{t('arboPot.geenFacturen')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.open_facturen.map((f) => {
              const genoeg = data.saldo >= f.total_amount;
              return (
                <li key={f.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-navy-900">
                      {f.invoice_number} · {euro(f.total_amount)}
                    </p>
                    <p className={`text-xs ${f.status === 'overdue' ? 'text-red-700' : 'text-navy-500'}`}>
                      {t('arboPot.vervalt', { datum: datumF.format(new Date(f.due_date)) })}
                    </p>
                  </div>
                  {f.betaalbaar_uit_pot ? (
                    <button
                      type="button"
                      disabled={!genoeg || betaal.isPending}
                      title={genoeg ? undefined : t('arboPot.onvoldoende')}
                      onClick={() => {
                        if (window.confirm(t('arboPot.betaalZeker', { nummer: f.invoice_number, bedrag: euro(f.total_amount) }))) betaal.mutate(f.id);
                      }}
                      className="rounded-lg bg-green-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-50"
                    >
                      {genoeg ? t('arboPot.betaal') : t('arboPot.onvoldoende')}
                    </button>
                  ) : (
                    <span className="text-xs text-navy-500">{t('arboPot.nietUitPot')}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Grootboek */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <h2 className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-navy-900">{t('arboPot.mutaties')}</h2>
        {data.mutaties.length === 0 ? (
          <p className="p-4 text-sm text-navy-500">{t('arboPot.geenMutaties')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {data.mutaties.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-2 text-navy-500 whitespace-nowrap">{datumF.format(new Date(m.created_at))}</td>
                    <td className="px-4 py-2 text-navy-800">
                      {t(`arboPot.soort.${m.soort}`)}
                      {m.omschrijving && <span className="text-navy-500"> · {m.omschrijving}</span>}
                    </td>
                    <td className={`px-4 py-2 text-right font-semibold whitespace-nowrap ${m.bedrag >= 0 ? 'text-green-700' : 'text-navy-900'}`}>
                      {m.bedrag >= 0 ? '+' : ''}
                      {euro(m.bedrag)}
                    </td>
                    <td className="px-4 py-2 text-right text-navy-500 whitespace-nowrap">{euro(m.saldo_na)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};
